import { ref } from 'vue'
import axios from 'axios'
import { createTracedHeaders } from '@/util/datadogRum'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'
import { AnalyticsRequest } from '@/types/fcc'
import { AuthState } from '@/types/authState'

const ANALYTICS_REQUEST_TIMEOUT_MS = 120_000

export type AnalyticsKind =
  | 'volume'
  | 'auth-rate'
  | 'refund-rate'
  | 'large-transactions'
  | 'chargebacks'
  | 'bin-mismatch'

const buildHeaders = (token: string) => {
  const { headers } = createTracedHeaders({
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  })
  return headers
}

export const useFCCAnalytics = () => {
  const loading = ref(false)
  const error = ref<ApiError | null>(null)

  const getSeries = async <T>(
    kind: AnalyticsKind,
    authState: AuthState,
    request: AnalyticsRequest,
  ): Promise<T> => {
    loading.value = true
    error.value = null
    try {
      const response = await axios.get<T>(
        API_ENDPOINTS.FCC.ANALYTICS(
          kind,
          request.merchant_id,
          request.date_from,
          request.date_to,
        ),
        {
          headers: buildHeaders(authState.user?.accessToken ?? ''),
          timeout: ANALYTICS_REQUEST_TIMEOUT_MS,
        },
      )
      return response.data
    } catch (err) {
      error.value = normalizeError(err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getSeries,
  }
}
